import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';
import { hashPassword } from '../utils/sha256';

export interface AuthUser {
  id: number;
  username: string;
  displayName: string;
  role: 'admin' | 'user';
  studentId: number | null;
  mustChangePassword: boolean;
  hasSecurityQuestion?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<AuthUser>;
  register: (data: RegisterData) => Promise<AuthUser>;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  setSecurityQuestion: (question: string, answer: string) => Promise<void>;
  clearMustChangePassword: () => void;
}

export interface RegisterData {
  username: string;
  password: string;
  displayName: string;
  studentInfo?: {
    name: string;
    school?: string;
    grade?: string;
    education_level?: 'senior' | 'junior';
    subject_type?: string;
    grade_total?: number;
    target_level?: string;
    notes?: string;
  };
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'auth_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setMustChangePassword(false);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  // On mount: verify existing token
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const u = res.data.user;
        setUser(u);
        setMustChangePassword(u.mustChangePassword);
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (username: string, password: string): Promise<AuthUser> => {
    const hashedPassword = await hashPassword(password);
    const res = await api.post('/auth/login', { username, password: hashedPassword });
    const { token: newToken, user: u } = res.data;
    setToken(newToken);
    setUser(u);
    setMustChangePassword(u.mustChangePassword);
    localStorage.setItem(TOKEN_KEY, newToken);
    return u;
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<AuthUser> => {
    const hashedPassword = await hashPassword(data.password);
    const res = await api.post('/auth/register', { ...data, password: hashedPassword });
    const { token: newToken, user: u } = res.data;
    setToken(newToken);
    setUser(u);
    setMustChangePassword(false);
    localStorage.setItem(TOKEN_KEY, newToken);
    return u;
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    const [hashedOld, hashedNew] = await Promise.all([
      hashPassword(oldPassword),
      hashPassword(newPassword),
    ]);
    await api.put('/auth/change-password', { oldPassword: hashedOld, newPassword: hashedNew }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setMustChangePassword(false);
  }, [token]);

  const setSecurityQuestion = useCallback(async (question: string, answer: string) => {
    await api.put('/auth/security-question', { question, answer }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (user) {
      setUser({ ...user, hasSecurityQuestion: true });
    }
  }, [token, user]);

  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!user,
      mustChangePassword,
      loading,
      login,
      register,
      logout,
      changePassword,
      setSecurityQuestion,
      clearMustChangePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
